<script setup lang="ts">
import CompletedProgress from '@/vue/atoms/completed-progress.vue';
import {getVueLink, useRouterSectionParams} from '@/ts/composables/stateful/use-route-params';
import SessionTocItem from '@/vue/organisms/session-toc-item.vue';
import {useSessionId} from '@/ts/composables/stateful/use-session';
import {
  computed, getCurrentInstance,
  onMounted,
  ref,
} from 'vue';
import BackLink from '@/vue/molecules/back-link.vue';
import {useInterventionStore} from '@/ts/store/intervention-store';
import {pagesPath} from '@/ts/router/pages-path';
import {useScreenSize} from '@/ts/composables/stateful/use-screen-size';
import type {Page} from '@/ts/types/dto/interventions-dto';

const props = defineProps<{
  neverScaleDown?: boolean // possibility to disable menu-scale-down for legacy reasons
}>();

const nav = ref<HTMLElement>();
const tocItems = ref();

const intervention = useInterventionStore();
const {iid, sid, pid} = useRouterSectionParams();
const {session, order, completedPercent} = useSessionId(sid);
const wrapper = ref<HTMLElement>(null!);

const instance = getCurrentInstance();
const interventionLandingLink = getVueLink(pagesPath.intervention.interventionLanding, {iid: iid.value});

const {isMobileResolution} = useScreenSize();
const isMenuScaledDownUnlockedByUser = ref<boolean>(false);
const isMenuScaledDown = computed<boolean>(() => {
  return !props.neverScaleDown &&
      isMobileResolution.value &&
      !isMenuScaledDownUnlockedByUser.value &&
      (session.value?.pages?.length ?? 0) > 3;
});

const navId = computed<string>(():string => {
  return `session-navigation-${  instance?.uid}`;
});

const boundaryInlineStyle = ref<string>('');

function onClickShowAllMenuItems(): void {
  isMenuScaledDownUnlockedByUser.value = true;

  // focus first nav item (needed for keyboard control)
  if (tocItems.value?.[0]) { //eslint-disable-line
    tocItems.value[0].focus(); //eslint-disable-line
  }

  // ensure transition animation
  boundaryInlineStyle.value =
      nav.value?.offsetHeight ?
          `height: ${nav.value?.offsetHeight}px; overflow: hidden;`
          : 'auto';
  window.setTimeout(() => {
    boundaryInlineStyle.value = '';
  }, 500);
}

// Added function to handle clicking "Show Less"
function onClickShowLessMenuItems(): void {
  isMenuScaledDownUnlockedByUser.value = false;
  // Maybe focus the show all button again? Requires ref on that button.
}

// New computed property to get the pages to display
const displayedPages = computed<{ page: Page; index: number;}[]>(() => {
  const allPages = session.value?.pages ?? [];
  const currentPageIndex = pid.value - 1; // 0-based index
  const totalPages = allPages.length;

  if (!isMenuScaledDown.value || totalPages < 3) {
    // Show all pages if expanded or less than 3 pages total
    return allPages.map((page, i) => ({page, index: i + 1}));
  }

  let pagesToShow: { page: Page; index: number; }[] = [];

  if (currentPageIndex === 0) {
    // First page active: show 1 (current), 2 (neutral), 3 (faded)
    pagesToShow = [
      {page: allPages[0], index: 1},
      {page: allPages[1], index: 2},
      {page: allPages[2], index: 3},
    ];
  } else if (currentPageIndex === totalPages - 1) {
    // Last page active: show N-2 (complete, faded), N-1 (complete), N (current)
     pagesToShow = [
      {page: allPages[totalPages - 3], index: totalPages - 2},
      {page: allPages[totalPages - 2], index: totalPages - 1},
      {page: allPages[totalPages - 1], index: totalPages},
    ];
  } else {
    // Middle page active: show N-1 (complete), N (current), N+1 (faded)
    pagesToShow = [
      {page: allPages[currentPageIndex - 1], index: currentPageIndex},
      {page: allPages[currentPageIndex], index: currentPageIndex + 1},
      {page: allPages[currentPageIndex + 1], index: currentPageIndex + 2},
    ];
  }

  return pagesToShow;
});

onMounted(() => {
  wrapper.value.scrollTop = wrapper.value.scrollHeight;
});
</script>
<template>
  <div ref="wrapper">
    <back-link :label="intervention!.name" :to="interventionLandingLink!"/>
    <div class="session-index">
      {{ $t('session_index', {order}) }}
    </div>
    <completed-progress class="progress" :completed="completedPercent"/>
    <div
      class="nav-boundary"
      :class="{'nav-boundary--scaled-down': isMenuScaledDown}"
      :style="boundaryInlineStyle"
    >
      <nav :id="navId" ref="nav" :aria-label="$t('session_nav')">
        <ol ref="navList" class="nav-session-pages">
          <session-toc-item
            v-for="item in displayedPages"
            ref="tocItems"
            :key="item.page.id"
            :index="item.index"
            :page="item.page"
            :is-inactive="isMenuScaledDown && item.index !== pid"
          />
        </ol>
      </nav>
      <q-btn
        v-if="isMenuScaledDown"
        class="btn-show-all"
        :aria-expanded="!isMenuScaledDown"
        :aria-controls="navId"
        :aria-label="$t('session_nav_expand_btn_aria')"
        flat
        dense
        @click="onClickShowAllMenuItems"
      >
        <span class="text-body-sm-bold">{{ $t('session_nav_expand_btn') }}</span>
      </q-btn>
      <q-btn
        v-if="!isMenuScaledDown && isMenuScaledDownUnlockedByUser && isMobileResolution"
        class="btn-show-less"
        :aria-expanded="!isMenuScaledDown"
        :aria-controls="navId"
        :aria-label="$t('session_nav_collapse_btn_aria')"
        flat
        dense
        @click="onClickShowLessMenuItems"
      >
        <span class="text-body-sm-bold">{{ $t('session_nav_collapse_btn') }}</span>
      </q-btn>
    </div>
  </div>
</template>
<style scoped lang="scss">
.nav-session-pages {
  @include dashed-between-elements(var(--Spacing-spacing-2));
  width: 100%;
  margin: 0;
  padding: 0;
}

.nav-boundary {
  // has inline-style set by JavaScript in order to set height for the transition
  transition: height .3s linear;
  margin: var(--Spacing-spacing-5) -5px 0 -5px;
  padding: 5px; // give room for outline of :focus-visible (keyboard usage)

  &--scaled-down {
    position: relative;
  }
}

.session-index {
  @include font-body;
  justify-self: flex-end;
}

.progress {
  @include font-body-bold;
}

.btn-show-all {
  @include focus-visible;
  // position: absolute;
  // bottom: 10px;
  // z-index: 1;
  // left: 50%;
  // transform: translate(-50%, 0);

  // Center button horizontally and add space above
  display: block;
  margin: var(--Spacing-spacing-4) auto 0 auto;

  @include screen-lg {
    display: none;
  }
}

// Added style for show less button (can reuse show-all)
.btn-show-less {
  @include focus-visible;
  display: block;
  margin: var(--Spacing-spacing-4) auto 0 auto;

  @include screen-lg {
    display: none;
  }
}
</style>
